import React from "react";
import {
  SearchBox,
  Dropdown,
  Option,
  Button,
  Text,
  Caption1,
  Checkbox,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";

interface StatusFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  showHidden: boolean;
  onShowHiddenChange: (showHidden: boolean) => void;
  resultCount: number;
  hiddenCount: number;
  onClearFilters: () => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
  filterControl: {
    minWidth: "200px",
    flex: "1",
  },
  searchBox: {
    minWidth: "250px",
    flex: "2",
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginLeft: "auto",
  },
  resultCount: {
    color: tokens.colorNeutralForeground2,
  },
});

export const StatusFilters: React.FC<StatusFiltersProps> = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  showHidden,
  onShowHiddenChange,
  resultCount,
  hiddenCount,
  onClearFilters,
}) => {
  const styles = useStyles();

  const hasActiveFilters = search !== "" || statusFilter !== "all";

  const getStatusDisplayValue = () => {
    if (statusFilter === "all") return "All Status";
    if (statusFilter === "has_discussions") return "Has Discussions";
    return statusFilter;
  };

  return (
    <div className={styles.container}>
      <div className={styles.filtersRow}>
        {/* Search Box */}
        <SearchBox
          className={styles.searchBox}
          placeholder="Search status items..."
          value={search}
          onChange={(_e, data) => onSearchChange(data.value)}
        />

        {/* Status Filter */}
        <Dropdown
          className={styles.filterControl}
          placeholder="Filter by status"
          value={getStatusDisplayValue()}
          onOptionSelect={(_e, data) =>
            onStatusFilterChange(data.optionValue as string)
          }
        >
          <Option value="all">All Status</Option>
          <Option value="Open">Open</Option>
          <Option value="Processed">Processed</Option>
          <Option value="Rework Required">Rework Required</Option>
          <Option value="Quality Reviewed">Quality Reviewed</Option>
          <Option value="Partner Reviewed">Partner Reviewed</Option>
          <Option value="has_discussions">Has Discussions</Option>
        </Dropdown>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {hiddenCount > 0 && (
            <Checkbox
              checked={showHidden}
              onChange={(_e, data) => onShowHiddenChange(data.checked === true)}
              label={`Show hidden (${hiddenCount})`}
            />
          )}

          <Caption1 className={styles.resultCount}>
            {resultCount} {resultCount === 1 ? "item" : "items"}
          </Caption1>

          {hasActiveFilters && (
            <Button
              appearance="subtle"
              icon={<Dismiss20Regular />}
              onClick={onClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
